import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, AlertTriangle, Activity } from "lucide-react";

const LABELS = {
  webhook: "Webhook",
  dropbox: "Dropbox",
  brevo: "Brevo",
  stories_delivery: "Storie AI",
};

/** Compact real-time health indicator for admin users.
 * Green pill = everything OK. Red = something is down. Gray = no data yet.
 * Click to jump to Impostazioni → Stato sistema.
 * Fully responsive + single-line on mobile.
 */
export default function AdminHealthBadge() {
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/system-health");
      setHealth(data || {});
    } catch {
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    load();
    // Poll every 2 minutes so a staff sitting on dashboard sees fresh status
    const id = setInterval(load, 120_000);
    return () => clearInterval(id);
  }, [load]);

  if (!health) return null;

  const entries = Object.entries(health).filter(([, v]) => v && v.checked_at);
  const broken = entries.filter(([, v]) => v.healthy === false).map(([k]) => k);
  const hasData = entries.length > 0;
  const allHealthy = hasData && broken.length === 0;

  const tone = !hasData
    ? "gray"
    : broken.length > 0
    ? "red"
    : "green";

  const bgClass = {
    green: "bg-green-50 border-green-300 text-green-800",
    red: "bg-red-50 border-red-400 text-red-800",
    gray: "bg-[var(--mp-dash-bg)] border-[var(--mp-border)] text-[var(--mp-muted)]",
  }[tone];

  const Icon = tone === "red" ? AlertTriangle : tone === "green" ? CheckCircle2 : Activity;

  const summary = !hasData
    ? "Controllo non ancora eseguito"
    : allHealthy
    ? "Tutti i sistemi OK"
    : `${broken.length} problema${broken.length > 1 ? "i" : ""}: ${broken
        .map((k) => LABELS[k] || k)
        .join(", ")}`;

  return (
    <Link
      to="/dashboard/impostazioni"
      data-testid="admin-health-badge"
      title={summary}
      aria-label={`Stato sistema: ${summary}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-all hover:-translate-y-0.5 ${bgClass}`}
    >
      <span className={`relative flex h-2 w-2 ${tone === "red" ? "" : ""}`}>
        {tone === "red" && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-60" />
        )}
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            tone === "green"
              ? "bg-green-500"
              : tone === "red"
              ? "bg-red-500"
              : "bg-gray-400"
          }`}
        />
      </span>
      <Icon className="h-3 w-3 hidden sm:block" />
      <span className="whitespace-nowrap">
        {tone === "red" ? summary : tone === "green" ? "Sistemi OK" : "Sistema"}
      </span>
    </Link>
  );
}
